import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  setCurrentPage,
  setSelectedTournament,
} from "../../redux/admin/tournaments.slice";

import {
  fetchTournamentList,
  addTournament,
  editTournament,
} from "../../redux/admin/tournaments.thunk";

import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* ─── Spinner component ─── */
const Spinner = ({ size = 16, color = "#ffffff", thickness = 2 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    style={{ animation: "t-spin 0.72s linear infinite", flexShrink: 0 }}
    aria-hidden="true"
  >
    <circle
      cx="12" cy="12" r="10"
      stroke={color}
      strokeOpacity="0.22"
      strokeWidth={thickness}
    />
    <path
      d="M12 2a10 10 0 0 1 10 10"
      stroke={color}
      strokeWidth={thickness}
      strokeLinecap="round"
    />
  </svg>
);

/* ─── Skeleton shimmer ─── */
const Skeleton = ({ w = "100%", h = 14, r = 6, style: extra = {} }) => (
  <div
    style={{
      width: w, height: h, borderRadius: r,
      background: "linear-gradient(90deg,#f0f0f5 25%,#e8e8ed 50%,#f0f0f5 75%)",
      backgroundSize: "600px 100%",
      animation: "t-shimmer 1.4s infinite linear",
      ...extra,
    }}
  />
);

const Tournaments = () => {
  const dispatch = useDispatch();
  const limit = 10;

  const frontFileRef = useRef(null);
  const backFileRef  = useRef(null);

  const {
    tournaments    = [],
    totalPages     = 1,
    currentPage    = 1,
    total          = 0,
    loading        = false,
    selectedTournament = null,
  } = useSelector((state) => state.admin?.tournaments || {});

  const [searchTerm,      setSearchTerm]      = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeFilter,    setActiveFilter]    = useState("all");

  const [showModal,   setShowModal]   = useState(false);
  const [isEditMode,  setIsEditMode]  = useState(false);
  const [activeTab,   setActiveTab]   = useState("basic");

  /* ── NEW: saving state ── */
  const [saving, setSaving] = useState(false);

  const initialCategoryRow = () => ({ id: Date.now() + Math.random(), category_name: "" });
  const [tournamentCategories, setTournamentCategories] = useState([initialCategoryRow()]);

  const initialFormState = {
    category_id: "",
    name: "",
    event_date: "",
    venue: "",
    description: "",
    prospectus_image: null,
    prospectus_back_image: null,
    entry_fee: "",
    additional_fee: "",
    additional_fee_label: "CDCA Fee",
    entry_close_at: "",
  };

  const [formData,        setFormData]        = useState(initialFormState);
  const [frontPreview,    setFrontPreview]    = useState(null);
  const [backPreview,     setBackPreview]     = useState(null);
  const [isFrontReplaced, setIsFrontReplaced] = useState(false);
  const [isBackReplaced,  setIsBackReplaced]  = useState(false);

  const categoryOptions = [{ id: 1, name: "Chess" }];
  const IMAGE_BASE_URL  = "https://aadhavacademy.com/api/";

  /* ── Debounce ── */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 600);
    return () => clearTimeout(t);
  }, [searchTerm]);

  /* ── Fetch list ── */
  useEffect(() => {
    dispatch(fetchTournamentList({
      page: currentPage, limit,
      search: debouncedSearch,
      category_id: activeFilter === "all" ? "" : activeFilter,
    }));
  }, [dispatch, currentPage, debouncedSearch, activeFilter]);

  const handlePageChange   = (page) => { if (page >= 1 && page <= totalPages) dispatch(setCurrentPage(page)); };
  const handleFilterChange = (val)  => { setActiveFilter(val); dispatch(setCurrentPage(1)); };
  const handleChange       = (e)    => { const { name, value } = e.target; setFormData(p => ({ ...p, [name]: value })); };

  /* ── Category rows ── */
  const handleCategoryRowChange = (id, value) =>
    setTournamentCategories(p => p.map(r => r.id === id ? { ...r, category_name: value } : r));
  const handleAddCategoryRow    = () => setTournamentCategories(p => [...p, initialCategoryRow()]);
  const handleRemoveCategoryRow = (id) => {
    if (tournamentCategories.length === 1) return;
    setTournamentCategories(p => p.filter(r => r.id !== id));
  };

  const getCategoryPill = (id) => ({ 1: "pill-amber", 2: "pill-green", 3: "pill-blue", 4: "pill-coral" }[parseInt(id)] || "pill-blue");
  const getCategoryName = (id) => (categoryOptions.find(c => c.id === parseInt(id))?.name || "Open");

  const resolvePreviewUrl = (img) => {
    if (!img || typeof img !== "string") return null;
    if (img.startsWith("data:") || img.startsWith("blob:") || img.startsWith("http://") || img.startsWith("https://")) return img;
    return `${IMAGE_BASE_URL}${img.startsWith("/") ? img.slice(1) : img}`;
  };

  const formatPrice = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? "0.00" : n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDateDisplay = (d) => {
    if (!d) return "TBD";
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0]; if (!file) return;
    setFormData(p => ({ ...p, [field]: file }));
    if (field === "prospectus_image") setIsFrontReplaced(true); else setIsBackReplaced(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      if (field === "prospectus_image") setFrontPreview(reader.result); else setBackPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeSelectedFile = (field) => {
    setFormData(p => ({ ...p, [field]: null }));
    if (field === "prospectus_image") {
      setFrontPreview(null); setIsFrontReplaced(true);
      if (frontFileRef.current) frontFileRef.current.value = "";
    } else {
      setBackPreview(null); setIsBackReplaced(true);
      if (backFileRef.current) backFileRef.current.value = "";
    }
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setFormData(initialFormState);
    setFrontPreview(null); setBackPreview(null);
    setIsFrontReplaced(false); setIsBackReplaced(false);
    setTournamentCategories([initialCategoryRow()]);
    setActiveTab("basic");
    setShowModal(true);
  };

  const openEditModal = (tournament) => {
    setIsEditMode(true);
    dispatch(setSelectedTournament(tournament));
    const { prospectus_image, prospectus_back_image, ...rest } = tournament;
    setFormData({ ...initialFormState, ...rest, prospectus_image: null, prospectus_back_image: null });
    setFrontPreview(resolvePreviewUrl(prospectus_image));
    setBackPreview(resolvePreviewUrl(prospectus_back_image));
    setIsFrontReplaced(false); setIsBackReplaced(false);
    const existingCats =
      Array.isArray(tournament.tournament_categories) && tournament.tournament_categories.length > 0
        ? tournament.tournament_categories.map(c => ({
            id: c.id ?? Date.now() + Math.random(),
            category_name: c.category_name ?? "",
            db_id: c.id ?? null,
          }))
        : [initialCategoryRow()];
    setTournamentCategories(existingCats);
    setActiveTab("basic");
    setShowModal(true);
  };

  /* ── SUBMIT (with loader) ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.event_date || !formData.venue) {
      toastr.warning("Name, event date, and venue are required.");
      return;
    }

    setSaving(true);
    try {
      const dataPayload = new FormData();
      Object.keys(formData).forEach(key => {
        if (key !== "prospectus_image" && key !== "prospectus_back_image") {
          if (formData[key] !== null && formData[key] !== undefined)
            dataPayload.append(key, formData[key]);
        }
      });

      const validCategories = tournamentCategories
        .filter(c => c.category_name.trim() !== "")
        .map(({ category_name, db_id }) => ({ category_name, ...(db_id ? { id: db_id } : {}) }));
      dataPayload.append("tournament_categories", JSON.stringify(validCategories));

      if (isEditMode) {
        dataPayload.append("is_front_replaced", isFrontReplaced ? "1" : "0");
        dataPayload.append("is_back_replaced",  isBackReplaced  ? "1" : "0");
        if (isFrontReplaced && formData.prospectus_image)
          dataPayload.append("prospectus_image", formData.prospectus_image);
        if (isBackReplaced && formData.prospectus_back_image)
          dataPayload.append("prospectus_back_image", formData.prospectus_back_image);
      } else {
        if (formData.prospectus_image)      dataPayload.append("prospectus_image",      formData.prospectus_image);
        if (formData.prospectus_back_image) dataPayload.append("prospectus_back_image", formData.prospectus_back_image);
      }

      if (isEditMode) {
        await dispatch(editTournament({ id: selectedTournament.id, tournamentData: dataPayload })).unwrap();
        toastr.success("Tournament updated.");
      } else {
        await dispatch(addTournament(dataPayload)).unwrap();
        toastr.success("Tournament created.");
      }

      setShowModal(false);
      dispatch(fetchTournamentList({
        page: currentPage, limit, search: debouncedSearch,
        category_id: activeFilter === "all" ? "" : activeFilter,
      }));
    } catch (err) {
      toastr.error(err || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const filledCategoryCount = tournamentCategories.filter(c => c.category_name.trim() !== "").length;

  const renderPagination = () =>
    Array.from({ length: totalPages }, (_, i) => i + 1).map(i => (
      <button
        key={i} type="button"
        onClick={() => handlePageChange(i)}
        className={`t-page-btn${currentPage === i ? " active" : ""}`}
      >{i}</button>
    ));

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  return (
    <div className="t-page">
      <div className="t-wrap">

        {/* ── PAGE HEADER ── */}
        <div className="t-header">
          <div>
            <p className="t-eyebrow">Aadhav Academy · Admin Console</p>
            <h1 className="t-title">Tournament Registry</h1>
          </div>
          <button type="button" className="t-btn-primary" onClick={openAddModal}>
            <i className="fas fa-plus"></i> New Tournament
          </button>
        </div>

        {/* ── KPI STRIP ── */}
        <div className="t-kpi-grid">
          {[
            { label: "Total tournaments", value: total },
            { label: "Showing",           value: tournaments.length },
            { label: "Current page",      value: `${currentPage} / ${totalPages}` },
          ].map(({ label, value }) => (
            <div key={label} className="t-kpi">
              <span className="t-kpi-label">{label}</span>
              <span className="t-kpi-val">{loading ? <Skeleton w={40} h={22} r={5} /> : value}</span>
            </div>
          ))}
        </div>

        {/* ── TOOLBAR ── */}
        <div className="t-toolbar">
          <div className="t-search">
            <i className="fas fa-search t-search-icon"></i>
            <input
              type="text"
              placeholder="Search by name or venue…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="t-filter-tabs">
            {[{ id: "all", name: "All" }, ...categoryOptions].map(({ id, name }) => (
              <button
                key={id} type="button"
                className={`t-ftab${activeFilter === String(id) ? " active" : ""}`}
                onClick={() => handleFilterChange(String(id))}
              >{name}</button>
            ))}
          </div>
        </div>

        {/* ── DATA TABLE ── */}
        <div className="t-card">
          <div className="table-responsive">
            <table className="t-table">
              <thead>
                <tr>
                  <th style={{ width: "35%" }}>Tournament</th>
                  <th style={{ width: "12%" }}>Category</th>
                  <th style={{ width: "16%" }}>Event date</th>
                  <th style={{ width: "22%" }}>Fees</th>
                  <th className="text-end" style={{ width: "15%" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  /* ── Skeleton rows ── */
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="t-row">
                      <td>
                        <div className="t-name-cell">
                          <Skeleton w={34} h={34} r={8} style={{ flexShrink: 0 }} />
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
                            <Skeleton w="65%" h={12} />
                            <Skeleton w="45%" h={10} />
                          </div>
                        </div>
                      </td>
                      <td><Skeleton w={60} h={22} r={20} /></td>
                      <td><Skeleton w={80} h={12} /><Skeleton w={60} h={10} style={{ marginTop: 5 }} /></td>
                      <td><Skeleton w={70} h={12} /><Skeleton w={90} h={10} style={{ marginTop: 5 }} /></td>
                      <td style={{ textAlign: "right" }}><Skeleton w={52} h={30} r={7} style={{ marginLeft: "auto" }} /></td>
                    </tr>
                  ))
                ) : tournaments.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="t-empty-state">
                      <div className="t-empty-icon-wrap">
                        <i className="fas fa-inbox"></i>
                      </div>
                      <p className="t-empty-title">No tournaments found</p>
                      <span className="t-empty-desc">Try a different search term or add a new tournament.</span>
                    </td>
                  </tr>
                ) : (
                  tournaments.map(t => (
                    <tr key={t.id} className="t-row">
                      <td>
                        <div className="t-name-cell">
                          <div className="t-avatar">{t.name?.[0]?.toUpperCase() || "T"}</div>
                          <div>
                            <div className="t-name">{t.name}</div>
                            <div className="t-venue">
                              <i className="fas fa-map-marker-alt"></i> {t.venue}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`t-pill ${getCategoryPill(t.category_id)}`}>
                          {getCategoryName(t.category_id)}
                        </span>
                      </td>
                      <td>
                        <div className="t-date-main">{formatDateDisplay(t.event_date)}</div>
                        {t.entry_close_at && (
                          <div className="t-date-close">Closes {t.entry_close_at.split(" ")[0]}</div>
                        )}
                      </td>
                      <td>
                        <div className="t-fee-main">₹{formatPrice(t.entry_fee)}</div>
                        {parseFloat(t.additional_fee) > 0 && (
                          <div className="t-fee-add">
                            + ₹{formatPrice(t.additional_fee)} {t.additional_fee_label || "Add-on"}
                          </div>
                        )}
                      </td>
                      <td className="text-end">
                        <button type="button" className="t-btn-outline" onClick={() => openEditModal(t)}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── PAGINATION ── */}
          <div className="t-footer">
            <span className="t-count">{tournaments.length} of {total} records</span>
            <div className="t-pagination">
              <button type="button" className="t-page-btn t-page-arrow"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}>
                <i className="fas fa-chevron-left"></i>
              </button>
              {renderPagination()}
              <button type="button" className="t-page-btn t-page-arrow"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════
          MODAL
      ═══════════════════════════════════════ */}
      {showModal && (
        <div
          className="t-modal-overlay"
          onClick={e => e.target === e.currentTarget && !saving && setShowModal(false)}
        >
          <div className="t-modal">

            {/* Sidebar */}
            <div className="t-modal-side">
              <div className="t-modal-side-header">
                <p className="t-modal-side-title">Tournament profile</p>
                <p className="t-modal-side-sub">Configuration workspace</p>
              </div>
              <nav className="t-modal-nav">
                {[
                  { key: "basic",      icon: "fas fa-sliders-h",   label: "Basic details" },
                  { key: "fees",       icon: "fas fa-receipt",     label: "Fee structure" },
                  { key: "categories", icon: "fas fa-layer-group", label: "Categories" },
                  { key: "prospectus", icon: "fas fa-images",      label: "Prospectus images" },
                ].map(({ key, icon, label }) => (
                  <button
                    key={key} type="button"
                    className={`t-nav-item${activeTab === key ? " active" : ""}`}
                    onClick={() => setActiveTab(key)}
                    disabled={saving}
                  >
                    <i className={icon}></i>
                    <span>{label}</span>
                    {key === "categories" && filledCategoryCount > 0 && (
                      <span className="t-nav-badge">{filledCategoryCount}</span>
                    )}
                  </button>
                ))}
              </nav>
            </div>

            {/* Main panel */}
            <form onSubmit={handleSubmit} className="t-modal-main">
              <div className="t-modal-header">
                <div>
                  <h2 className="t-modal-title">
                    {isEditMode ? "Edit tournament" : "New tournament"}
                  </h2>
                  <p className="t-modal-sub">
                    {isEditMode
                      ? "Update the details below and save."
                      : "Fill in the details below to register a new event."}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-close shadow-none"
                  onClick={() => setShowModal(false)}
                  aria-label="Close"
                  disabled={saving}
                ></button>
              </div>

              <div className="t-modal-body">
                <div className="row g-3">

                  {/* ── BASIC DETAILS ── */}
                  {activeTab === "basic" && (
                    <>
                      <div className="col-md-6">
                        <label className="t-label">Tournament name *</label>
                        <input type="text" name="name" className="t-field"
                          placeholder="e.g. State Chess Championship 2025"
                          value={formData.name} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="t-label">Sports category</label>
                        <select name="category_id" className="t-field"
                          value={formData.category_id} onChange={handleChange}>
                          <option value="">Select a category</option>
                          {categoryOptions.map(item => (
                            <option key={item.id} value={item.id}>{item.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="t-label">Event date *</label>
                        <input type="date" name="event_date" className="t-field"
                          value={formData.event_date} onChange={handleChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="t-label">Registration deadline</label>
                        <input type="datetime-local" name="entry_close_at" className="t-field"
                          value={formData.entry_close_at} onChange={handleChange} />
                      </div>
                      <div className="col-12">
                        <label className="t-label">Venue *</label>
                        <input type="text" name="venue" className="t-field"
                          placeholder="Full address of the event venue"
                          value={formData.venue} onChange={handleChange} />
                      </div>
                      <div className="col-12">
                        <label className="t-label">Description</label>
                        <textarea rows="3" name="description" className="t-field"
                          placeholder="Rules, eligibility criteria, and any additional notes…"
                          value={formData.description} onChange={handleChange} />
                      </div>
                    </>
                  )}

                  {/* ── FEE STRUCTURE ── */}
                  {activeTab === "fees" && (
                    <>
                      <div className="col-12">
                        <div className="t-section-note">
                          Set the base entry fee and any additional charges that apply to this tournament.
                        </div>
                      </div>
                      <div className="col-md-4">
                        <label className="t-label">Base entry fee (₹)</label>
                        <input type="number" name="entry_fee" placeholder="0.00"
                          className="t-field" value={formData.entry_fee} onChange={handleChange} />
                      </div>
                      <div className="col-md-4">
                        <label className="t-label">Additional fee (₹)</label>
                        <input type="number" name="additional_fee" placeholder="0.00"
                          className="t-field" value={formData.additional_fee} onChange={handleChange} />
                      </div>
                      <div className="col-md-4">
                        <label className="t-label">Fee label</label>
                        <input type="text" name="additional_fee_label" className="t-field"
                          placeholder="e.g. CDCA Fee"
                          value={formData.additional_fee_label} onChange={handleChange} />
                      </div>
                      {(formData.entry_fee || formData.additional_fee) && (
                        <div className="col-12">
                          <div className="t-fee-preview">
                            <span className="t-fee-preview-label">Total per entry</span>
                            <span className="t-fee-preview-val">
                              ₹{formatPrice((parseFloat(formData.entry_fee) || 0) + (parseFloat(formData.additional_fee) || 0))}
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* ── CATEGORIES ── */}
                  {activeTab === "categories" && (
                    <>
                      <div className="col-12">
                        <div className="t-section-note">
                          Define the age or skill divisions for this tournament.
                          Each row is one category (e.g. Under 9, Under 14, Open).
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="t-cat-table-head">
                          <span className="t-cat-col-no">#</span>
                          <span className="t-cat-col-name">Category name</span>
                          <span className="t-cat-col-action"></span>
                        </div>
                        <div className="t-cat-list">
                          {tournamentCategories.map((row, index) => (
                            <div key={row.id} className="t-cat-row">
                              <span className="t-cat-col-no t-cat-serial">{index + 1}</span>
                              <div className="t-cat-col-name">
                                <input type="text" className="t-field"
                                  placeholder={`e.g. ${["Under 9", "Under 14", "Under 19", "Open"][index] || "Category name"}`}
                                  value={row.category_name}
                                  onChange={e => handleCategoryRowChange(row.id, e.target.value)} />
                              </div>
                              <div className="t-cat-col-action">
                                <button type="button" className="t-cat-remove-btn"
                                  onClick={() => handleRemoveCategoryRow(row.id)}
                                  disabled={tournamentCategories.length === 1}
                                  title={tournamentCategories.length === 1 ? "At least one category is required" : "Remove"}>
                                  <i className="fas fa-times"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <button type="button" className="t-cat-add-btn" onClick={handleAddCategoryRow}>
                          <i className="fas fa-plus"></i> Add category
                        </button>
                      </div>
                      {filledCategoryCount > 0 && (
                        <div className="col-12">
                          <div className="t-cat-preview">
                            <span className="t-cat-preview-label">Preview</span>
                            <div className="t-cat-preview-pills">
                              {tournamentCategories.filter(c => c.category_name.trim()).map(c => (
                                <span key={c.id} className="t-pill pill-blue">{c.category_name}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* ── PROSPECTUS IMAGES ── */}
                  {activeTab === "prospectus" && (
                    <>
                      <div className="col-12">
                        <div className="t-section-note">
                          Upload the front and back cover images for the tournament prospectus
                          leaflet. Accepted formats: PNG, JPG, WebP · Max 5 MB each.
                        </div>
                      </div>
                      {/* Front */}
                      <div className="col-sm-6">
                        <label className="t-label">Front cover</label>
                        <input type="file" ref={frontFileRef} accept="image/*" className="d-none"
                          onChange={e => handleFileChange(e, "prospectus_image")} />
                        {!frontPreview ? (
                          <div className="t-dropzone"
                            onClick={() => frontFileRef.current.click()}
                            role="button" tabIndex={0}
                            onKeyDown={e => e.key === "Enter" && frontFileRef.current.click()}>
                            <i className="fas fa-cloud-upload-alt t-dropzone-icon"></i>
                            <p>Click to upload front cover</p>
                          </div>
                        ) : (
                          <div className="t-img-preview">
                            <img src={frontPreview} alt="Front cover preview"
                              onError={e => { e.target.onerror = null; e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='120'%3E%3Crect width='200' height='120' fill='%23f1f5f9'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='12' fill='%2394a3b8'%3EImage not found%3C/text%3E%3C/svg%3E"; }} />
                            <div className="t-img-overlay">
                              <button type="button" className="t-btn-remove"
                                onClick={() => removeSelectedFile("prospectus_image")}>
                                <i className="fas fa-trash-alt"></i> Remove
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Back */}
                      <div className="col-sm-6">
                        <label className="t-label">Back cover</label>
                        <input type="file" ref={backFileRef} accept="image/*" className="d-none"
                          onChange={e => handleFileChange(e, "prospectus_back_image")} />
                        {!backPreview ? (
                          <div className="t-dropzone"
                            onClick={() => backFileRef.current.click()}
                            role="button" tabIndex={0}
                            onKeyDown={e => e.key === "Enter" && backFileRef.current.click()}>
                            <i className="fas fa-cloud-upload-alt t-dropzone-icon"></i>
                            <p>Click to upload back cover</p>
                          </div>
                        ) : (
                          <div className="t-img-preview">
                            <img src={backPreview} alt="Back cover preview"
                              onError={e => { e.target.onerror = null; e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='120'%3E%3Crect width='200' height='120' fill='%23f1f5f9'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='12' fill='%2394a3b8'%3EImage not found%3C/text%3E%3C/svg%3E"; }} />
                            <div className="t-img-overlay">
                              <button type="button" className="t-btn-remove"
                                onClick={() => removeSelectedFile("prospectus_back_image")}>
                                <i className="fas fa-trash-alt"></i> Remove
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                </div>
              </div>

              {/* ── MODAL FOOTER with loader ── */}
              <div className="t-modal-footer">
                <button
                  type="button"
                  className="t-btn-cancel"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="t-btn-primary t-btn-submit"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Spinner size={15} color="#ffffff" thickness={2} />
                      {isEditMode ? "Saving changes…" : "Creating…"}
                    </>
                  ) : (
                    isEditMode ? "Save changes" : "Create tournament"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── STYLES ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,400;0,14..32,500;0,14..32,600;1,14..32,400&display=swap');

        /* ── Keyframes ── */
        @keyframes t-spin {
          to { transform: rotate(360deg); }
        }
        @keyframes t-shimmer {
          0%   { background-position: -600px 0; }
          100% { background-position:  600px 0; }
        }
        @keyframes t-modal-in {
          from { opacity: 0; transform: scale(0.97) translateY(10px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);     }
        }
        @keyframes t-overlay-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        /* ── BASE ── */
        .t-page {
          background: #f5f5f7;
          min-height: 100vh;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          color: #1d1d1f;
          font-size: 14px;
        }
        .t-wrap {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem 1.5rem;
        }

        /* ── PAGE HEADER ── */
        .t-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 1.5rem;
        }
        .t-eyebrow {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.07em;
          color: #aeaeb2;
          margin: 0 0 4px;
        }
        .t-title {
          font-size: 1.35rem;
          font-weight: 600;
          color: #1d1d1f;
          letter-spacing: -0.022em;
          margin: 0;
        }

        /* ── KPI STRIP ── */
        .t-kpi-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          margin-bottom: 1.25rem;
        }
        @media (max-width: 640px) { .t-kpi-grid { grid-template-columns: repeat(2, 1fr); } }
        .t-kpi {
          background: #fff;
          border: 1px solid #d2d2d7;
          border-radius: 12px;
          padding: 14px 18px;
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        .t-kpi-label {
          font-size: 11px;
          color: #6e6e73;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .t-kpi-val {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1d1d1f;
          letter-spacing: -0.025em;
          font-variant-numeric: tabular-nums;
          min-height: 1.8rem;
          display: flex;
          align-items: center;
        }

        /* ── TOOLBAR ── */
        .t-toolbar {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }
        .t-search { flex: 1; min-width: 200px; position: relative; }
        .t-search-icon {
          position: absolute; left: 12px; top: 50%;
          transform: translateY(-50%);
          color: #aeaeb2; font-size: 12px;
        }
        .t-search input {
          width: 100%;
          background: #fff;
          border: 1px solid #d2d2d7;
          border-radius: 10px;
          padding: 9px 12px 9px 34px;
          font-size: 13px;
          color: #1d1d1f;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
          font-family: inherit;
        }
        .t-search input:focus {
          border-color: #0071e3;
          box-shadow: 0 0 0 3px rgba(0,113,227,0.15);
        }
        .t-search input::placeholder { color: #aeaeb2; }

        .t-filter-tabs {
          flex: 1; min-width: 0;
          display: flex; gap: 3px;
          overflow-x: auto;
          background: #fff;
          border: 1px solid #d2d2d7;
          border-radius: 10px;
          padding: 3px;
          scrollbar-width: none;
        }
        .t-filter-tabs::-webkit-scrollbar { display: none; }
        .t-ftab {
          border: none; background: transparent;
          padding: 5px 13px; border-radius: 7px;
          font-size: 12.5px; color: #6e6e73;
          cursor: pointer; font-weight: 400;
          white-space: nowrap; flex-shrink: 0;
          height: 28px; display: inline-flex; align-items: center;
          transition: all 0.12s; font-family: inherit;
        }
        .t-ftab.active {
          background: #f5f5f7; color: #1d1d1f;
          font-weight: 500; border: 1px solid #d2d2d7;
        }
        .t-ftab:hover:not(.active) { color: #3c3c43; }

        /* ── PRIMARY BUTTON ── */
        .t-btn-primary {
          background: #0071e3;
          color: #fff;
          border: none;
          border-radius: 10px;
          padding: 9px 18px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          transition: background 0.15s, opacity 0.15s;
          white-space: nowrap;
          font-family: inherit;
          min-width: 140px;
          justify-content: center;
        }
        .t-btn-primary:hover:not(:disabled) { background: #0077ed; }
        .t-btn-primary:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }

        /* Submit button specific (slightly taller in modal footer) */
        .t-btn-submit {
          padding: 9px 22px;
          min-width: 158px;
        }

        /* ── TABLE CARD ── */
        .t-card {
          background: #fff;
          border: 1px solid #d2d2d7;
          border-radius: 14px;
          overflow: hidden;
        }
        .t-table { width: 100%; border-collapse: collapse; }
        .t-table thead th {
          background: #f5f5f7;
          font-size: 10.5px;
          font-weight: 600;
          color: #6e6e73;
          text-transform: uppercase;
          letter-spacing: 0.07em;
          padding: 11px 18px;
          border-bottom: 1px solid #e8e8ed;
          white-space: nowrap;
        }
        .t-table tbody tr {
          border-bottom: 1px solid #f5f5f7;
          transition: background 0.1s;
        }
        .t-table tbody tr:last-child { border-bottom: none; }
        .t-row:hover { background: #fafafa; }
        .t-table td { padding: 13px 18px; vertical-align: middle; }

        .t-name-cell { display: flex; align-items: center; gap: 10px; }
        .t-avatar {
          width: 34px; height: 34px;
          border-radius: 8px;
          background: #f5f5f7;
          border: 1px solid #e8e8ed;
          color: #3c3c43;
          font-size: 13px; font-weight: 600;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .t-name { font-size: 13px; font-weight: 500; color: #1d1d1f; }
        .t-venue { font-size: 11.5px; color: #6e6e73; margin-top: 2px; }
        .t-venue i { font-size: 10.5px; margin-right: 2px; }

        /* ── PILLS ── */
        .t-pill {
          display: inline-block;
          padding: 3px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 500;
          white-space: nowrap;
        }
        .pill-amber { background: #fef3c7; color: #92400e; border: 1px solid #fde68a; }
        .pill-green { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
        .pill-blue  { background: #dbeafe; color: #1e40af; border: 1px solid #bfdbfe; }
        .pill-coral { background: #fee2e2; color: #991b1b; border: 1px solid #fecaca; }

        /* ── DATES & FEES ── */
        .t-date-main { font-size: 12.5px; font-weight: 500; color: #1d1d1f; font-variant-numeric: tabular-nums; }
        .t-date-close { font-size: 11px; color: #ef4444; margin-top: 2px; }
        .t-fee-main  { font-size: 12.5px; font-weight: 500; color: #1d1d1f; font-variant-numeric: tabular-nums; }
        .t-fee-add   { font-size: 11.5px; color: #6e6e73; margin-top: 2px; }

        /* ── OUTLINE BUTTON ── */
        .t-btn-outline {
          border: 1px solid #d2d2d7; background: #fff;
          border-radius: 8px; padding: 6px 14px;
          font-size: 12px; font-weight: 500; color: #3c3c43;
          cursor: pointer; transition: all 0.12s; font-family: inherit;
        }
        .t-btn-outline:hover { border-color: #aeaeb2; background: #f5f5f7; color: #1d1d1f; }

        /* ── EMPTY STATE ── */
        .t-empty-state { text-align: center; padding: 3rem 1rem; }
        .t-empty-icon-wrap {
          width: 52px; height: 52px;
          background: #f5f5f7; border-radius: 14px;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 1.4rem; color: #aeaeb2; margin-bottom: 12px;
        }
        .t-empty-title { font-size: 14px; font-weight: 500; color: #3c3c43; margin: 0 0 4px; }
        .t-empty-desc  { font-size: 12px; color: #aeaeb2; }

        /* ── PAGINATION ── */
        .t-footer {
          display: flex; align-items: center; justify-content: space-between;
          padding: 12px 18px; border-top: 1px solid #f5f5f7;
        }
        .t-count { font-size: 12px; color: #6e6e73; font-weight: 500; }
        .t-pagination { display: flex; gap: 4px; }
        .t-page-btn {
          min-width: 30px; height: 30px; padding: 0 6px;
          border: 1px solid #e8e8ed; background: #fff;
          border-radius: 7px; font-size: 12px; font-weight: 500; color: #3c3c43;
          cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
          transition: all 0.12s; font-family: inherit;
        }
        .t-page-btn:hover:not(:disabled):not(.active) { background: #f5f5f7; border-color: #d2d2d7; }
        .t-page-btn.active { background: #0071e3; color: #fff; border-color: #0071e3; }
        .t-page-btn:disabled { opacity: 0.3; cursor: not-allowed; }

        /* ── MODAL ── */
        .t-modal-overlay {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.36);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center;
          z-index: 9999; padding: 16px;
          animation: t-overlay-in 0.2s ease;
        }
        .t-modal {
          width: 100%; max-width: 900px; height: 590px;
          background: #fff; border-radius: 18px;
          border: 1px solid #d2d2d7;
          box-shadow: 0 32px 60px -12px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.06);
          display: flex; overflow: hidden;
          animation: t-modal-in 0.24s cubic-bezier(0.34,1.1,0.64,1);
        }

        /* Sidebar */
        .t-modal-side {
          width: 210px; background: #f5f5f7;
          border-right: 1px solid #e8e8ed;
          display: flex; flex-direction: column; flex-shrink: 0;
        }
        .t-modal-side-header { padding: 18px 16px; border-bottom: 1px solid #e8e8ed; }
        .t-modal-side-title { font-size: 13px; font-weight: 600; color: #1d1d1f; margin: 0; }
        .t-modal-side-sub   { font-size: 11px; color: #aeaeb2; margin: 3px 0 0; }
        .t-modal-nav { padding: 8px; display: flex; flex-direction: column; gap: 2px; }
        .t-nav-item {
          display: flex; align-items: center; gap: 9px;
          padding: 9px 12px; border-radius: 9px;
          border: none; background: transparent;
          font-size: 13px; color: #6e6e73; font-weight: 400;
          cursor: pointer; text-align: left; width: 100%;
          transition: all 0.12s; position: relative; font-family: inherit;
        }
        .t-nav-item i { font-size: 12.5px; width: 14px; text-align: center; flex-shrink: 0; }
        .t-nav-item span { flex: 1; }
        .t-nav-item:hover:not(.active):not(:disabled) { background: #ebebf0; color: #3c3c43; }
        .t-nav-item.active {
          background: #fff; color: #0071e3;
          font-weight: 500; border: 1px solid #e8e8ed;
        }
        .t-nav-item:disabled { opacity: 0.5; cursor: not-allowed; }

        .t-nav-badge {
          background: #0071e3; color: #fff;
          font-size: 10px; font-weight: 600;
          border-radius: 20px; padding: 1px 6px;
          line-height: 1.6; flex-shrink: 0;
        }

        /* Main panel */
        .t-modal-main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
        .t-modal-header {
          padding: 18px 26px; border-bottom: 1px solid #f5f5f7;
          display: flex; align-items: flex-start; justify-content: space-between;
        }
        .t-modal-title { font-size: 15px; font-weight: 600; color: #1d1d1f; margin: 0; letter-spacing: -0.015em; }
        .t-modal-sub   { font-size: 12px; color: #6e6e73; margin: 3px 0 0; }
        .t-modal-body  { flex: 1; overflow-y: auto; padding: 22px 26px; }
        .t-modal-footer {
          padding: 14px 26px; border-top: 1px solid #f5f5f7;
          display: flex; justify-content: flex-end; align-items: center; gap: 10px;
          background: #f5f5f7;
        }

        /* ── Form fields ── */
        .t-label {
          display: block; font-size: 11px; font-weight: 600;
          color: #6e6e73; text-transform: uppercase;
          letter-spacing: 0.05em; margin-bottom: 5px;
        }
        .t-field {
          width: 100%; border: 1px solid #d2d2d7; border-radius: 9px;
          padding: 9px 12px; font-size: 13px; color: #1d1d1f;
          background: #fff; outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
          font-family: inherit;
        }
        .t-field:focus {
          border-color: #0071e3;
          box-shadow: 0 0 0 3px rgba(0,113,227,0.15);
        }
        .t-field::placeholder { color: #aeaeb2; }
        select.t-field { cursor: pointer; }
        textarea.t-field { resize: vertical; }

        .t-section-note {
          background: #f5f5f7; border: 1px solid #e8e8ed;
          border-radius: 9px; padding: 10px 14px;
          font-size: 12.5px; color: #6e6e73; line-height: 1.6;
        }

        .t-fee-preview {
          background: #e8f0fe; border: 1px solid #bfdbfe;
          border-radius: 9px; padding: 10px 16px;
          display: flex; align-items: center; justify-content: space-between;
        }
        .t-fee-preview-label { font-size: 12.5px; color: #1e40af; font-weight: 500; }
        .t-fee-preview-val {
          font-size: 15px; font-weight: 600; color: #1e40af;
          font-variant-numeric: tabular-nums;
        }

        /* ── CATEGORY TAB ── */
        .t-cat-table-head {
          display: flex; align-items: center; gap: 10px;
          padding: 0 0 8px 0; border-bottom: 1px solid #e8e8ed;
          margin-bottom: 8px; font-size: 10px; font-weight: 700;
          color: #aeaeb2; text-transform: uppercase; letter-spacing: 0.07em;
        }
        .t-cat-col-no     { flex: 0 0 36px; text-align: center; }
        .t-cat-col-name   { flex: 1; }
        .t-cat-col-action { flex: 0 0 36px; }
        .t-cat-list {
          display: flex; flex-direction: column; gap: 8px;
          margin-bottom: 10px; max-height: 220px; overflow-y: auto; padding-right: 2px;
        }
        .t-cat-list::-webkit-scrollbar { width: 3px; }
        .t-cat-list::-webkit-scrollbar-track { background: transparent; }
        .t-cat-list::-webkit-scrollbar-thumb { background: #d2d2d7; border-radius: 99px; }
        .t-cat-row { display: flex; align-items: center; gap: 10px; }
        .t-cat-serial {
          flex: 0 0 36px; text-align: center;
          font-size: 12px; color: #aeaeb2; font-variant-numeric: tabular-nums;
          background: #f5f5f7; border: 1px solid #e8e8ed; border-radius: 7px;
          height: 38px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .t-cat-remove-btn {
          flex: 0 0 36px; width: 36px; height: 36px;
          display: inline-flex; align-items: center; justify-content: center;
          border: 1px solid #fecaca; background: #fff;
          color: #ef4444; border-radius: 8px;
          cursor: pointer; font-size: 11.5px;
          transition: all 0.12s; flex-shrink: 0;
        }
        .t-cat-remove-btn:hover:not(:disabled) { background: #fef2f2; border-color: #ef4444; }
        .t-cat-remove-btn:disabled { opacity: 0.3; cursor: not-allowed; border-color: #e8e8ed; color: #d2d2d7; }
        .t-cat-add-btn {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          width: 100%; padding: 9px 16px;
          border: 1.5px dashed #d2d2d7; background: transparent;
          border-radius: 9px; font-size: 13px; font-weight: 500;
          color: #6e6e73; cursor: pointer; transition: all 0.15s;
          margin-top: 4px; font-family: inherit;
        }
        .t-cat-add-btn:hover { border-color: #0071e3; color: #0071e3; background: #e8f0fe; }
        .t-cat-add-btn i { font-size: 11px; }
        .t-cat-preview {
          background: #f5f5f7; border: 1px solid #e8e8ed;
          border-radius: 9px; padding: 10px 14px;
          display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
        }
        .t-cat-preview-label {
          font-size: 10px; font-weight: 700; color: #aeaeb2;
          text-transform: uppercase; letter-spacing: 0.07em; flex-shrink: 0;
        }
        .t-cat-preview-pills { display: flex; flex-wrap: wrap; gap: 6px; }

        /* ── IMAGE UPLOAD ── */
        .t-dropzone {
          border: 1.5px dashed #d2d2d7; border-radius: 12px;
          padding: 28px 16px; text-align: center; cursor: pointer;
          background: #f5f5f7; transition: all 0.15s;
          min-height: 170px; display: flex; flex-direction: column;
          align-items: center; justify-content: center;
        }
        .t-dropzone:hover { border-color: #0071e3; background: #e8f0fe; }
        .t-dropzone-icon { font-size: 1.6rem; color: #aeaeb2; margin-bottom: 10px; }
        .t-dropzone p { font-size: 13px; font-weight: 500; color: #3c3c43; margin: 0; }
        .t-img-preview {
          position: relative; border-radius: 12px; overflow: hidden;
          border: 1px solid #e8e8ed; background: #1d1d1f;
          min-height: 170px; max-height: 170px;
          display: flex; align-items: center; justify-content: center;
        }
        .t-img-preview img { width: 100%; height: 100%; object-fit: contain; max-height: 168px; }
        .t-img-overlay {
          position: absolute; inset: 0;
          background: rgba(0,0,0,0.5);
          display: flex; align-items: center; justify-content: center;
          opacity: 0; transition: opacity 0.15s;
        }
        .t-img-preview:hover .t-img-overlay { opacity: 1; }
        .t-btn-remove {
          background: #ef4444; color: #fff; border: none;
          border-radius: 20px; padding: 6px 14px;
          font-size: 12px; font-weight: 500; cursor: pointer;
          display: inline-flex; align-items: center; gap: 5px; font-family: inherit;
        }
        .t-btn-remove:hover { background: #dc2626; }

        /* ── CANCEL BUTTON ── */
        .t-btn-cancel {
          border: 1px solid #d2d2d7; background: #fff;
          border-radius: 9px; padding: 9px 18px;
          font-size: 13px; font-weight: 500; color: #6e6e73;
          cursor: pointer; transition: all 0.12s; font-family: inherit;
        }
        .t-btn-cancel:hover:not(:disabled) { background: #f5f5f7; color: #3c3c43; }
        .t-btn-cancel:disabled { opacity: 0.5; cursor: not-allowed; }

        /* ── MODAL BODY SCROLLBAR ── */
        .t-modal-body::-webkit-scrollbar { width: 5px; }
        .t-modal-body::-webkit-scrollbar-track { background: transparent; }
        .t-modal-body::-webkit-scrollbar-thumb { background: #d2d2d7; border-radius: 3px; }

        /* ── RESPONSIVE ── */
        @media (max-width: 768px) {
          .t-modal { flex-direction: column; height: auto; max-height: 90vh; }
          .t-modal-side { width: 100%; border-right: none; border-bottom: 1px solid #e8e8ed; }
          .t-modal-side-header { display: none; }
          .t-modal-nav { flex-direction: row; overflow-x: auto; padding: 6px; scrollbar-width: none; }
          .t-modal-nav::-webkit-scrollbar { display: none; }
          .t-nav-item { white-space: nowrap; width: auto; font-size: 12px; padding: 7px 12px; }
          .t-modal-body { padding: 16px; }
          .t-modal-footer { padding: 12px 16px; }
          .t-modal-header { padding: 14px 16px; }
        }
        @media (max-width: 480px) {
          .t-wrap { padding: 1rem; }
          .t-header { flex-direction: column; }
          .t-btn-primary { width: 100%; justify-content: center; }
          .t-toolbar { flex-direction: column; }
          .t-filter-tabs { width: 100%; }
          .t-modal-footer { flex-wrap: wrap; }
          .t-btn-submit { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default Tournaments;
